import { useCallback, useEffect, useMemo, useState } from 'react'

import { RevenueCatPurchase } from './RevenuecatCheckout'
import { StandardSuccessComponent } from '@/components/success'
import { SubscriptionAwareProductFlow } from './redeem/SubscriptionAwareProductFlow'
import { AppShell } from './components/shell'

import { oauthCheckProductEligibility } from '@/services/checkout'
import { useOauthSubscriptions } from '@/services'
import { useTrackPaymentFlow } from './trackingPaymentFlow'

import { TRACKING_TYPES } from '@/constants/TrackingConstants'
import { Subscription, User } from '@/types'

function hasActiveSubscription(subscriptions: Subscription[]): boolean {
  return subscriptions.some((s) =>
    Object.values(s.entitlements)
      .flat()
      .some((e) => e.includes('PREMIUM'))
  )
}

interface PremiumCheckoutContentsProps {
  token: string
  refreshToken: string
  clientKey: string
  user: User
  subscriptions: Subscription[]
}

export function PremiumCheckoutContents({ token, refreshToken, clientKey, user, subscriptions: sessionSubscriptions }: PremiumCheckoutContentsProps) {
  const isSubscribed = hasActiveSubscription(sessionSubscriptions)
  const track = useTrackPaymentFlow()
  const { mutate: fetchSubscriptions } = useOauthSubscriptions()

  // Construct Stripe return URL — append params to current URL without re-encoding existing params
  // {CHECKOUT_SESSION_ID} is a Stripe template replaced with the actual session ID on redirect
  const stripeReturnUrl = useMemo(() => {
    const current = window.location.href.split('#')[0] // strip hash if any
    const separator = current.includes('?') ? '&' : '?'
    return `${current}${separator}stripeComplete=true&session_id={CHECKOUT_SESSION_ID}`
  }, [])

  // Promotion eligibility state
  const [eligiblePromotions, setEligiblePromotions] = useState<string[]>([])
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false)
  const [eligibilityChecked, setEligibilityChecked] = useState(false)
  const [apiError, setApiError] = useState<boolean>(false)

  // Post-checkout state
  const [checkoutComplete, setCheckoutComplete] = useState(isSubscribed)

  // Post credentials to parent window
  const postCredentialsToParent = useCallback((subs: Subscription[]) => {
    console.log('[ExternalLogin] Posting credentials to parent window')
    window.parent.postMessage(
      {
        userToken: token,
        refreshToken,
        id: user.id,
        duprId: user.referralCode,
        stats: user.stats,
        subscriptions: subs,
      },
      '*'
    )
  }, [token, refreshToken, user])

  // Handle RC purchase completion — fetch subs, post credentials, show success
  const handleRCPurchaseComplete = useCallback(() => {
    fetchSubscriptions(
      { token },
      {
        onSuccess: (res) => {
          track(TRACKING_TYPES.PAYMENT_COMPLETED_SUCCESS)
          postCredentialsToParent(res.subscriptions)
          setCheckoutComplete(true)
        },
        onError: () => {
          // Post credentials without subscriptions
          track(TRACKING_TYPES.PAYMENT_COMPLETED_SUCCESS)
          postCredentialsToParent([])
          setCheckoutComplete(true)
        },
      }
    )
  }, [token, fetchSubscriptions, postCredentialsToParent, track])

  // If already subscribed on mount, post credentials immediately
  useEffect(() => {
    if (isSubscribed) {
      postCredentialsToParent(sessionSubscriptions)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    track(TRACKING_TYPES.PAYMENT_STARTED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check product eligibility on component mount if user doesn't have active subscription
  useEffect(() => {
    const checkEligibility = async () => {
      if (isSubscribed || !token || !clientKey) {
        setEligibilityChecked(true)
        return
      }

      setIsCheckingEligibility(true)
      try {
        const products = await oauthCheckProductEligibility({
          token,
          request: { base64ClientKey: clientKey },
        })
        setEligiblePromotions(products)
      } catch (error) {
        console.error('Failed to check product eligibility:', error)
        setEligiblePromotions([])
        setApiError(true)
      } finally {
        setIsCheckingEligibility(false)
        setEligibilityChecked(true)
      }
    }

    checkEligibility()
  }, [token, clientKey, isSubscribed])

  // Checkout complete — show success (no upsell since they just subscribed)
  if (checkoutComplete) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <div className="flex flex-col items-center w-full min-h-[calc(100vh-4rem)] py-12 px-4">
          <p className="text-center text-[color:var(--ink-soft)] text-sm mb-2">Connected with</p>
          <p className="text-center text-[color:var(--ink)] text-3xl font-[family:var(--display-font)] tracking-[var(--display-tracking)] font-[number:var(--display-weight)] mb-8">
            DUPR
          </p>
          <div className="w-full max-w-md rounded-[var(--radius-card)] bg-[color:var(--surface)] border border-[color:var(--border)]">
            <StandardSuccessComponent
              type="payment"
              message="Your DUPR subscription is active, you may now close this window"
              showProductEligibility={false}
              base64ClientKey={clientKey}
            />
          </div>
        </div>
      </AppShell>
    )
  }

  // Show loading while checking eligibility
  if (isCheckingEligibility || !eligibilityChecked) {
    return (
      <AppShell logoLinksToDashboard={false}>
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--accent)] mx-auto mb-4" />
            <p className="text-[color:var(--ink-soft)]">Checking available subscription options...</p>
          </div>
        </div>
      </AppShell>
    )
  }

  // If we have eligible promotions and no API error, show promotion flow
  if (!apiError && eligiblePromotions.length > 0) {
    return <SubscriptionAwareProductFlow token={token} base64ClientKey={clientKey} promotionIds={eligiblePromotions} returnUrl={stripeReturnUrl} />
  }

  // Fallback to RevenueCat for all other cases
  return <RevenueCatPurchase isExternal={true} onPurchaseComplete={handleRCPurchaseComplete} />
}
